import { formatAboBV, formatAboPV, formatPercentage, formatInteger } from "../../components/Lit/acc-mixins/l10n.js";
import { noDecimalMarkets } from "../../components/Lit/acc-mixins/src/modules/l10n/constants.js";

/**
 * remove new line in the given stringValue
 * and replace multiple empty spaces to single empty space
 * end result will be single line of string
 * @param {String} stringValue
 */
export const toSingleLine = (stringValue: string) => {
  if (!stringValue) {
    return stringValue;
  }
  return stringValue
    .trim()
    .replace(/(?:\r\n|\r|\n)/g, "")
    .replace(/\s\s+/g, " ");
};

/**
 * Determines what formatting is used for KPI value. The default behavior is to format as an integer.
 *
 * @param {string} kpiKey
 * @param {string} kpiValue
 * @returns {string}
 */
export function formatKpiValue(kpiKey: string, kpiValue: number, market: string, locale: string): string {
  if (kpiKey.match(/percent/gi)) {
    return formatPercentage(Math.trunc(kpiValue) / 100, locale);
  } else if (kpiKey.match(/PV|pointsToNextLevel/gi)) {
    return formatAboPV(kpiValue, locale, market);
  } else if (kpiKey.match(/BV/gi)) {
    return formatAboBV(kpiValue, locale, market);
  } else {
    return formatInteger(kpiValue, locale);
  }
}

/**
 * Formats a string to be used as a string for a CSS ID
 * Example: "Personal PV" -> "personal-pv"
 * @param {string} str
 * @returns {string}
 */
export function formatToIDStr(str: string) {
  return str.replace(/\s+/g, "-").toLowerCase();
}

export const getNumberFormatter = (locale: string, market: string, options: Partial<Intl.NumberFormatOptions> = {}) => {
  const hasDecimals = !noDecimalMarkets.includes(market);
  const defaults: Intl.NumberFormatOptions = {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  };

  return new Intl.NumberFormat(locale, { ...defaults, ...options });
};

export const getLabelFormatter = (locale: string, market: string) => {
  return getNumberFormatter(market === "JP" ? locale : "en-US", market, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

export const capitalizeName = (str: string) => {
  const lowercaseParticles = new Set(["van", "von", "de", "da", "di", "le", "la", "del", "du", "dos", "des"]);

  return str
    .split(" ")
    .map((word, index) => {
      // If the word is a known lowercase particle (and not first), return it as-is
      if (index !== 0 && lowercaseParticles.has(word)) {
        return word;
      }

      // Handle hyphenated words
      return word
        .split("-")
        .map((part) => part.charAt(0).toLocaleUpperCase() + part.slice(1))
        .join("-");
    })
    .join(" ");
};
